import { useSession } from "@clerk/clerk-react";
import type {
  CustomCriterionScore,
  StageFeedbackCriterion,
  StageInterviewersDTO,
  SubmitFeedbackResponse,
} from "@shared/schema";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AutosizeTextarea } from "@/components/ui/autosize-textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { orpc, rpcClient } from "@/lib/rpc";

type Verdict = "YES" | "NO" | "UNKNOWN" | null;

export interface InterviewFeedbackData {
  interviewId: string;
  interviewStageId?: string | null;
  interviewerGroupId?: string | null;
  applicantName?: string;
  stageName?: string;
  jobOpeningId?: string;
  jobOpeningTitle?: string;
  applicationId?: string;
  allowAutoAdvance?: boolean;
  /** When present, the dialog will request an AI-drafted evaluation from the
   * transcript and pre-fill the form on open. */
  firefliesRecordingUrl?: string | null;
  meetingLink?: string | null;
  // Pre-fill values
  gcaVerdict?: Verdict;
  gcaNotes?: string;
  roleKnowledgeVerdict?: Verdict;
  roleKnowledgeNotes?: string;
  compWayVerdict?: Verdict;
  compWayNotes?: string;
  candidateExcitementScore?: number | null;
  candidateExcitementNote?: string | null;
  generalComments?: string;
  notesForNextInterviewer?: string;
  recommendation?: string | null;
  /** Snapshot of custom-form scores already submitted — pre-fills the form on
   * edit and defines which criteria the edit form renders. */
  customCriteriaScores?: CustomCriterionScore[] | null;
}

type EditableField = "gca" | "role" | "comp" | "general" | "next" | "decision";

type DraftUnavailableReason =
  | "no_url"
  | "pending"
  | "not_found"
  | "failed"
  | "skipped"
  | "empty"
  | "storage_error"
  | "not_connected"
  | "mcp_error"
  | "skill_error";

function isMeetingUrl(value: string): boolean {
  try {
    const host = new URL(value).hostname.toLowerCase();
    return (
      host === "meet.google.com" ||
      host.endsWith(".zoom.us") ||
      host === "zoom.us" ||
      host === "app.fireflies.ai" ||
      host === "fireflies.ai"
    );
  } catch {
    return false;
  }
}

/**
 * Strip query strings, fragments, and trailing slashes from a meeting URL.
 *
 * The upstream `find_meeting_transcript` MCP looks up meetings by literal
 * string match against `meeting_link` as stored by the Fireflies ingest
 * pipeline — which strips Google's `?authuser=...`, `?hs=...`, `#auth=...`,
 * etc. before storing. If we pass the URL with those bits, the lookup
 * misses. Normalize both ways and the lookup hits.
 */
function normalizeMeetingUrl(value: string): string {
  try {
    const u = new URL(value);
    u.search = "";
    u.hash = "";
    let s = u.toString();
    if (s.endsWith("/")) s = s.slice(0, -1);
    return s;
  } catch {
    return value;
  }
}

interface DraftStatusMeta {
  label: string;
  detail: string;
  tone: "ready" | "generating" | "failed" | "warn" | "idle";
}

function draftStatusMeta(reason: DraftUnavailableReason | undefined): DraftStatusMeta {
  switch (reason) {
    case "pending":
      return {
        label: "Transcrição ainda em processamento",
        detail: "· o Fireflies ainda está processando essa reunião",
        tone: "warn",
      };
    case "not_found":
      return {
        label: "Reunião não encontrada",
        detail: "· confirme se é o link do Google Meet/Zoom da entrevista",
        tone: "failed",
      };
    case "skipped":
      return {
        label: "Transcrição não foi gerada",
        detail: "· call muito curta ou silent meeting",
        tone: "failed",
      };
    case "failed":
      return {
        label: "Falha na ingestão da transcrição",
        detail: "· o Fireflies marcou essa reunião como falha",
        tone: "failed",
      };
    case "empty":
      return {
        label: "Transcrição vazia",
        detail: "· a reunião foi processada mas o conteúdo está vazio",
        tone: "failed",
      };
    case "storage_error":
      return {
        label: "Transcrição ainda não sincronizada",
        detail: "· o Fireflies reconhece a reunião, mas o conteúdo não sincronizou",
        tone: "warn",
      };
    case "not_connected":
      return {
        label: "Reunião privada no Fireflies",
        detail: "· conecte sua chave pessoal do Fireflies para acessar suas reuniões privadas",
        tone: "warn",
      };
    case "mcp_error":
      return {
        label: "Erro ao consultar Fireflies",
        detail: "· tente de novo em alguns instantes",
        tone: "failed",
      };
    case "skill_error":
      return {
        label: "Erro ao gerar rascunho",
        detail: "· a IA não conseguiu gerar o rascunho da transcrição",
        tone: "failed",
      };
    case "no_url":
      return {
        label: "Sem reunião",
        detail: "· cole o link do Google Meet para gerar rascunho",
        tone: "warn",
      };
    default:
      return { label: "", detail: "", tone: "idle" };
  }
}

interface InterviewFeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  interview: InterviewFeedbackData | null;
  isEditing?: boolean;
  onSuccess?: () => void;
}

function validateFeedbackForm(fields: {
  decision: "advance" | "reject" | null;
  gcaVerdict: Verdict;
  gcaNotes: string;
  roleKnowledgeVerdict: Verdict;
  roleKnowledgeNotes: string;
  compWayVerdict: Verdict;
  compWayNotes: string;
  autoAdvance: boolean;
  allowAutoAdvanceFlag: boolean;
  advanceMode: string;
  nextInterviewerSelection: { type: string; id: string } | null;
  /** Custom-form criteria for the stage; non-null replaces the default GCA /
   * Role Knowledge / COMP Way validation with per-criterion score checks. */
  customCriteria: StageFeedbackCriterion[] | null;
  criteriaScores: Record<string, { score: number | null; notes: string }>;
}): string | null {
  if (fields.decision === null) return "Selecione sua decisão: Aprovar ou Rejeitar";
  if (fields.customCriteria) {
    for (const criterion of fields.customCriteria) {
      const entry = fields.criteriaScores[criterion.id];
      if (entry?.score == null) return `Avalie o critério "${criterion.label}"`;
      if (criterion.requireNotes && !entry.notes.trim())
        return `Preencha o comentário do critério "${criterion.label}"`;
    }
    const effectiveAutoAdvanceCustom =
      fields.decision === "advance" && fields.autoAdvance && fields.allowAutoAdvanceFlag;
    if (
      effectiveAutoAdvanceCustom &&
      fields.advanceMode === "choose" &&
      !fields.nextInterviewerSelection
    )
      return "Selecione o próximo entrevistador ou escolha outro modo de avanço";
    return null;
  }
  if (fields.gcaVerdict === null) return "Selecione a avaliação de General Cognitive Ability";
  if (fields.gcaVerdict !== "UNKNOWN" && !fields.gcaNotes.trim())
    return "Preencha o comentário de General Cognitive Ability";
  if (fields.roleKnowledgeVerdict === null)
    return "Selecione a avaliação de Role-Related Knowledge";
  if (fields.roleKnowledgeVerdict !== "UNKNOWN" && !fields.roleKnowledgeNotes.trim())
    return "Preencha o comentário de Role-Related Knowledge";
  if (fields.compWayVerdict === null) return "Selecione a avaliação de COMP Way";
  if (fields.compWayVerdict !== "UNKNOWN" && !fields.compWayNotes.trim())
    return "Preencha o comentário de COMP Way";
  if (
    fields.gcaVerdict === "UNKNOWN" &&
    fields.roleKnowledgeVerdict === "UNKNOWN" &&
    fields.compWayVerdict === "UNKNOWN"
  )
    return "Pelo menos um dos atributos (GCA, Role-Related Knowledge ou COMP Way) deve ter avaliação Sim ou Não. Não é permitido marcar todos como N/A.";
  const effectiveAutoAdvance =
    fields.decision === "advance" && fields.autoAdvance && fields.allowAutoAdvanceFlag;
  if (effectiveAutoAdvance && fields.advanceMode === "choose" && !fields.nextInterviewerSelection)
    return "Selecione o próximo entrevistador ou escolha outro modo de avanço";
  return null;
}

type StageInterviewersEntry = StageInterviewersDTO;

function buildNextStageOptions(
  data: StageInterviewersEntry[] | undefined,
  currentStageId: string | null | undefined,
): { type: "individual" | "group"; id: string; label: string; count: number }[] {
  if (!data || !currentStageId) return [];
  const currentIdx = data.findIndex((s) => s.stageId === currentStageId);
  if (currentIdx < 0 || currentIdx >= data.length - 1) return [];
  const nextStage = data[currentIdx + 1];
  const options: { type: "individual" | "group"; id: string; label: string; count: number }[] = [];
  for (const emp of nextStage?.employees || []) {
    options.push({
      type: "individual",
      id: emp.id,
      label: emp.fullName,
      count: emp.interviewCount,
    });
  }
  for (const group of nextStage?.groups || []) {
    if (group.members.length === 0) continue;
    const names = group.members.map((m) => m.fullName).join(" e ");
    const maxCount = Math.max(...group.members.map((m) => m.interviewCount), 0);
    options.push({ type: "group", id: group.id, label: `Grupo: ${names}`, count: maxCount });
  }
  return options;
}

// ------------------------------------------------------------------
// Reusable presentational primitives — match the v2 redesign spec.
// Token roles (preserved from refero research):
//   - tan #faf5ec / rule #e6d3ae = AI-authored content ONLY. Fades on edit.
//   - black #0a0a0a = primary CTA + active/selected state ONLY.
// ------------------------------------------------------------------

function VerdictChips({
  value,
  onChange,
  testIdPrefix,
}: {
  value: Verdict;
  onChange: (v: Verdict) => void;
  testIdPrefix: string;
}) {
  const opt = (v: Exclude<Verdict, null>, label: string, testIdSuffix: string) => {
    const on = value === v;
    return (
      <button
        type="button"
        onClick={() => onChange(v)}
        data-testid={`button-${testIdPrefix}-${testIdSuffix}`}
        className={[
          "h-6 rounded-full px-3 text-[11px] font-medium leading-none",
          "border transition-all duration-150 ease-out",
          "tracking-[-0.0125em]",
          on
            ? "bg-foreground text-background border-foreground"
            : "bg-background text-muted-foreground border-border hover:-translate-y-px hover:text-foreground hover:border-foreground/30",
        ].join(" ")}
      >
        {label}
      </button>
    );
  };
  return (
    <div className="inline-flex gap-1" role="radiogroup">
      {opt("YES", "Sim", "yes")}
      {opt("UNKNOWN", "Sem sinal", "na")}
      {opt("NO", "Não", "no")}
    </div>
  );
}

function AiField({
  isAi,
  edited,
  children,
}: {
  isAi: boolean;
  edited: boolean;
  children: React.ReactNode;
}) {
  // Faint tan tint + 2px left rule signals AI-authored. Tint fades on first edit.
  // Tan token role per refero research: AI-suggested content (Rox).
  return (
    <div
      className={[
        "rounded-[10px] transition-colors duration-200 ease-out",
        isAi && !edited
          ? "bg-[#faf5ec] shadow-[inset_2px_0_0_#e6d3ae] dark:bg-[#1f1b14] dark:shadow-[inset_2px_0_0_#5c4a26]"
          : "",
      ].join(" ")}
    >
      {children}
    </div>
  );
}

function MicroFeedback({
  value,
  onChange,
  disabled,
}: {
  value: "good" | "bad" | null;
  onChange: (v: "good" | "bad") => void;
  disabled?: boolean;
}) {
  const chip = (v: "good" | "bad", label: string, testIdSuffix: string) => {
    const on = value === v;
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange(v)}
        data-testid={`button-ai-feedback-${testIdSuffix}`}
        className={[
          "h-[22px] rounded-full px-[9px] text-[10.5px] font-medium leading-none",
          "border transition-all duration-150 ease-out tracking-[-0.0125em]",
          on
            ? "bg-foreground text-background border-foreground"
            : "bg-background text-muted-foreground border-border hover:-translate-y-px hover:bg-muted hover:text-foreground",
          disabled ? "opacity-50 cursor-not-allowed" : "",
        ].join(" ")}
      >
        {label}
      </button>
    );
  };
  return (
    <div className="mt-2 flex items-center gap-1.5 text-[11px] text-muted-foreground">
      <span className="mr-0.5">Rascunho útil?</span>
      {chip("good", "Sim", "good")}
      {chip("bad", "Não", "bad")}
    </div>
  );
}

export function InterviewFeedbackDialog({
  open,
  onOpenChange,
  interview,
  isEditing = false,
  onSuccess,
}: InterviewFeedbackDialogProps) {
  const { toast } = useToast();

  const [gcaVerdict, setGcaVerdict] = useState<Verdict>(null);
  const [gcaNotes, setGcaNotes] = useState("");
  const [roleKnowledgeVerdict, setRoleKnowledgeVerdict] = useState<Verdict>(null);
  const [roleKnowledgeNotes, setRoleKnowledgeNotes] = useState("");
  const [compWayVerdict, setCompWayVerdict] = useState<Verdict>(null);
  const [compWayNotes, setCompWayNotes] = useState("");
  // Interviewer's reading of the candidate's excitement about Comp.
  // Optional; null means "didn't rate" → falls back to the IA-inferred value.
  const [candidateExcitementScore, setCandidateExcitementScore] = useState<number | null>(null);
  const [candidateExcitementNote, setCandidateExcitementNote] = useState("");
  const [generalComments, setGeneralComments] = useState("");
  const [notesForNextInterviewer, setNotesForNextInterviewer] = useState("");
  // Scores for the stage's custom feedback criteria, keyed by criterion id.
  const [criteriaScores, setCriteriaScores] = useState<
    Record<string, { score: number | null; notes: string }>
  >({});
  const [decision, setDecision] = useState<"advance" | "reject" | null>(null);
  const [autoAdvance, setAutoAdvance] = useState(true);
  const [advanceMode, setAdvanceMode] = useState<"auto" | "choose" | "delegate">("auto");
  const [nextInterviewerSelection, setNextInterviewerSelection] = useState<{
    type: "individual" | "group";
    id: string;
  } | null>(null);

  // ---- AI draft state ----
  const [aiDraftApplied, setAiDraftApplied] = useState(false);
  const [editedFields, setEditedFields] = useState<Set<EditableField>>(new Set());
  // Single overall utility rating asked once at the end of the form (was a
  // per-field chip on each AI-authored field — removed because it added
  // friction without much marginal signal over the implicit edit-diff that
  // the rejection-analysis digest already consumes).
  const [overallAiFeedback, setOverallAiFeedback] = useState<"good" | "bad" | null>(null);
  // Meeting URL input — used on the transcript-only card when no URL is stored.
  const [firefliesUrlInput, setFirefliesUrlInput] = useState("");
  // Inline "connect your personal Fireflies key" flow, shown when the org key
  // can't see a (host-private) meeting and the user hasn't linked their own key.
  const [firefliesKeyInput, setFirefliesKeyInput] = useState("");
  const [showKeyInput, setShowKeyInput] = useState(false);

  const { session } = useSession();

  // Evaluation-mode flow. Opening an unfilled evaluation presents a choice
  // (my inputs / transcript-only / manual) instead of auto-generating; editing
  // an existing one skips straight to the form.
  type FlowStep = "choice" | "inputs" | "form";
  const [flowStep, setFlowStep] = useState<FlowStep>("form");
  const [interviewerInputs, setInterviewerInputs] = useState("");
  // Reveal the inline meeting-URL field on the transcript-only card when no
  // transcript URL is stored yet.
  const [showTranscriptUrlField, setShowTranscriptUrlField] = useState(false);

  const hasStoredTranscript = Boolean(interview?.firefliesRecordingUrl);

  // Stage config — tells us which feedback form (default or a named template)
  // this stage uses.
  const stageConfigQueryEnabled =
    open && !isEditing && !!interview?.jobOpeningId && !!interview?.interviewStageId;
  const { data: jobStagesForForm, isPending: stageConfigPending } = useQuery({
    ...orpc.jobOpenings.getInterviewStages.queryOptions({
      input: { id: interview?.jobOpeningId ?? "" },
    }),
    enabled: stageConfigQueryEnabled,
  });
  const { data: jobFeedbackFormsList, isPending: feedbackFormsPending } = useQuery({
    ...orpc.jobOpenings.listFeedbackForms.queryOptions({
      input: { id: interview?.jobOpeningId ?? "" },
    }),
    enabled: stageConfigQueryEnabled,
  });
  // Gate the form until the stage config is known — otherwise a custom-form
  // stage briefly shows the AI-draft choice screen / default form, and a fast
  // user could submit a default payload the server will reject.
  const stageConfigLoading =
    stageConfigQueryEnabled && (stageConfigPending || feedbackFormsPending);

  // Custom-form criteria to render. On edit they come from the submitted
  // snapshot (the form as it was when first filled); on a fresh submission,
  // from the stage's feedback-form template.
  const customFormCriteria = useMemo<StageFeedbackCriterion[] | null>(() => {
    if (isEditing && interview?.customCriteriaScores?.length) {
      return interview.customCriteriaScores.map((s) => ({
        id: s.criterionId,
        label: s.label,
        scaleType: s.scaleType,
        scaleMin: s.scaleType === "numeric" ? (s.scaleMin ?? 0) : undefined,
        scaleMax: s.scaleType === "numeric" ? (s.scaleMax ?? undefined) : undefined,
        requireNotes: s.requireNotes,
      }));
    }
    if (isEditing) return null;
    const stage = jobStagesForForm?.find((s) => s.id === interview?.interviewStageId);
    if (!stage?.feedbackFormId) return null;
    const form = jobFeedbackFormsList?.find((f) => f.id === stage.feedbackFormId);
    const criteria = form?.criteria;
    return criteria && criteria.length > 0 ? criteria : null;
  }, [isEditing, interview, jobStagesForForm, jobFeedbackFormsList]);
  const hasCustomForm = !!customFormCriteria;

  type DraftPayload = {
    gca: { verdict: NonNullable<Verdict>; notes: string };
    role: { verdict: NonNullable<Verdict>; notes: string };
    comp: { verdict: NonNullable<Verdict>; notes: string };
    general: string;
    next: string;
    decision: "advance" | "reject";
    /** AI-suggested candidate excitement, derived from the transcript. Absent on
     * drafts cached before this field existed; score null when the skill
     * abstained. Pre-fills the editable excitement field only. */
    excitement?: { score: number | null; note: string } | null;
  };

  const applyDraft = useCallback((draft: DraftPayload) => {
    setGcaVerdict(draft.gca.verdict);
    setGcaNotes(draft.gca.notes);
    setRoleKnowledgeVerdict(draft.role.verdict);
    setRoleKnowledgeNotes(draft.role.notes);
    setCompWayVerdict(draft.comp.verdict);
    setCompWayNotes(draft.comp.notes);
    setGeneralComments(draft.general);
    setNotesForNextInterviewer(draft.next);
    setDecision(draft.decision);
    // Pre-fill the interviewer's excitement read with the AI suggestion. Only
    // when the skill produced a score — otherwise leave the field untouched so
    // we don't clobber whatever was prefilled from a saved value.
    if (draft.excitement && draft.excitement.score != null) {
      setCandidateExcitementScore(draft.excitement.score);
      setCandidateExcitementNote(draft.excitement.note ?? "");
    }
    setAiDraftApplied(true);
    setEditedFields(new Set());
  }, []);

  // Explicit draft generation, triggered from the choice/inputs steps. Carries
  // the chosen mode + optional interviewer notes; can also persist a pasted
  // meeting URL first (transcript_only with no stored URL).
  const draftMutation = useMutation({
    mutationFn: async (vars: {
      mode: "transcript_only" | "with_inputs";
      inputs?: string;
      url?: string;
    }) => {
      if (!interview) throw new Error("Entrevista indisponível");
      if (vars.url) {
        if (!isMeetingUrl(vars.url)) {
          throw new Error(
            "Link inválido — cole o link da reunião do Google Meet (https://meet.google.com/…) ou Zoom.",
          );
        }
        await rpcClient.interviews.setFirefliesUrl({
          interviewId: interview.interviewId,
          firefliesUrl: normalizeMeetingUrl(vars.url),
        });
      }
      // The Clerk client session is only needed for the org-wide transcript
      // path (the token is forwarded as bearer to the Comp MCP). Users
      // authenticated purely through the Express-session bridge (e.g. under
      // impersonation) have no Clerk client session — do NOT hard-fail here.
      // Send an empty token and let the backend fall back to the cached
      // transcript / personal Fireflies key paths, exactly like the Slack flow.
      // Fireflies-viewer URLs never touch the org path anyway, so they keep
      // working regardless of the Clerk session.
      const clerkToken = session ? ((await session.getToken()) ?? "") : "";
      return rpcClient.interviews.generateDraft({
        interviewId: interview.interviewId,
        clerkToken,
        mode: vars.mode,
        interviewerInputs: vars.inputs,
      });
    },
    onSuccess: (data) => {
      if (!data?.available) {
        const reason = data?.reason;
        const meta = draftStatusMeta(reason);
        if (reason === "not_connected") {
          // Host-private meeting the org key can't see — reveal the inline key
          // input so the user can link their own Fireflies key right here.
          setShowKeyInput(true);
          setFlowStep("form");
          toast({ title: meta.label, description: meta.detail.replace(/^· /, "") });
          return;
        }
        toast({
          title: meta.label || "Sem rascunho disponível",
          description: meta.detail.replace(/^· /, ""),
          variant: "destructive",
        });
        return;
      }
      applyDraft(data.draft);
      setFirefliesUrlInput("");
      setShowTranscriptUrlField(false);
      setFlowStep("form");
    },
    onError: (err: Error) => {
      toast({
        title: "Erro ao gerar rascunho",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const markEdited = (field: EditableField) => {
    if (!aiDraftApplied) return;
    setEditedFields((prev) => {
      if (prev.has(field)) return prev;
      const next = new Set(prev);
      next.add(field);
      return next;
    });
  };

  // Reset and pre-fill when interview changes
  useEffect(() => {
    if (interview && open) {
      setGcaVerdict(interview.gcaVerdict ?? null);
      setGcaNotes(interview.gcaNotes || "");
      setRoleKnowledgeVerdict(interview.roleKnowledgeVerdict ?? null);
      setRoleKnowledgeNotes(interview.roleKnowledgeNotes || "");
      setCompWayVerdict(interview.compWayVerdict ?? null);
      setCompWayNotes(interview.compWayNotes || "");
      setCandidateExcitementScore(interview.candidateExcitementScore ?? null);
      setCandidateExcitementNote(interview.candidateExcitementNote || "");
      setCriteriaScores(
        Object.fromEntries(
          (interview.customCriteriaScores ?? []).map((s) => [
            s.criterionId,
            { score: s.score, notes: s.notes ?? "" },
          ]),
        ),
      );
      setGeneralComments(interview.generalComments || "");
      setNotesForNextInterviewer(interview.notesForNextInterviewer || "");
      setDecision(
        interview.recommendation === "advance" || interview.recommendation === "reject"
          ? interview.recommendation
          : null,
      );
      setAutoAdvance(true);
      setAdvanceMode("auto");
      setNextInterviewerSelection(null);
      setAiDraftApplied(false);
      setEditedFields(new Set());
      setOverallAiFeedback(null);
      const prefillUrl =
        !interview.firefliesRecordingUrl &&
        interview.meetingLink &&
        isMeetingUrl(interview.meetingLink)
          ? interview.meetingLink
          : "";
      setFirefliesUrlInput(prefillUrl);
      setShowTranscriptUrlField(Boolean(prefillUrl));
      setFirefliesKeyInput("");
      setShowKeyInput(false);
      setInterviewerInputs("");
      // Editing an existing evaluation lands directly on the form; a fresh
      // evaluation starts on the mode-choice screen.
      setFlowStep(isEditing ? "form" : "choice");
    }
  }, [interview, open, isEditing]);

  // Custom-form stages skip the AI-draft choice screen — the draft skill only
  // produces the default Comp criteria, so it doesn't apply here.
  useEffect(() => {
    if (open && hasCustomForm && !isEditing) setFlowStep("form");
  }, [open, hasCustomForm, isEditing]);

  // Posts the overall rating to the legacy `aiDraftFeedback` column. Fires on
  // click — we don't wait for the response; the submit handler also passes
  // the snapshot below as a safety net.
  const submitAiDraftFeedbackMutation = useMutation({
    mutationFn: async (feedback: "good" | "bad") => {
      if (!interview) return;
      await rpcClient.interviews.submitAiDraftFeedback({
        interviewId: interview.interviewId,
        feedback,
      });
    },
  });

  const setOverallFeedback = (value: "good" | "bad") => {
    setOverallAiFeedback(value);
    submitAiDraftFeedbackMutation.mutate(value);
  };

  // Connect the user's personal Fireflies key, then re-run the draft fetch so
  // the host-private meeting resolves through their own key.
  const connectFirefliesMutation = useMutation({
    mutationFn: async (apiKey: string) => {
      return rpcClient.profile.connectFireflies({ apiKey });
    },
    onSuccess: async () => {
      setFirefliesKeyInput("");
      setShowKeyInput(false);
      toast({ title: "Fireflies conectado", description: "Gerando rascunho…" });
      // Re-run transcript-only generation now that the host-private meeting
      // resolves through the personal key.
      setAiDraftApplied(false);
      draftMutation.mutate({ mode: "transcript_only" });
    },
    onError: (err: Error) => {
      toast({
        title: "Não foi possível conectar",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const isDraftLoading = draftMutation.isPending;

  const setGcaVerdictE = (v: Verdict) => {
    setGcaVerdict(v);
    markEdited("gca");
  };
  const setRoleKnowledgeVerdictE = (v: Verdict) => {
    setRoleKnowledgeVerdict(v);
    markEdited("role");
  };
  const setCompWayVerdictE = (v: Verdict) => {
    setCompWayVerdict(v);
    markEdited("comp");
  };
  const setDecisionE = (v: "advance" | "reject") => {
    setDecision(v);
    markEdited("decision");
  };

  const resetForm = () => {
    setGcaVerdict(null);
    setGcaNotes("");
    setRoleKnowledgeVerdict(null);
    setRoleKnowledgeNotes("");
    setCompWayVerdict(null);
    setCompWayNotes("");
    setCandidateExcitementScore(null);
    setCandidateExcitementNote("");
    setCriteriaScores({});
    setGeneralComments("");
    setNotesForNextInterviewer("");
    setDecision(null);
    setAutoAdvance(true);
    setAdvanceMode("auto");
    setNextInterviewerSelection(null);
    setAiDraftApplied(false);
    setEditedFields(new Set());
    setOverallAiFeedback(null);
    setFirefliesUrlInput("");
    setFirefliesKeyInput("");
    setShowKeyInput(false);
    setInterviewerInputs("");
    setShowTranscriptUrlField(false);
    setFlowStep("form");
  };

  const allowAutoAdvanceFlag = interview?.allowAutoAdvance !== false;

  const { data: stageInterviewersData } = useQuery({
    ...orpc.jobOpenings.getStageInterviewers.queryOptions({
      input: { id: interview?.jobOpeningId ?? "" },
    }),
    enabled:
      !!interview?.jobOpeningId &&
      decision === "advance" &&
      autoAdvance &&
      allowAutoAdvanceFlag &&
      advanceMode === "choose",
  });

  const nextStageOptions = buildNextStageOptions(
    stageInterviewersData,
    interview?.interviewStageId,
  );

  const submitFeedback = useMutation({
    mutationFn: async (interviewId: string) => {
      // Snapshot which fields the manager touched. Only sent when an AI draft
      // was actually applied — without an AI baseline, "edited" has no meaning.
      const editedSnapshot = aiDraftApplied
        ? {
            gca: editedFields.has("gca"),
            role: editedFields.has("role"),
            comp: editedFields.has("comp"),
            general: editedFields.has("general"),
            next: editedFields.has("next"),
            decision: editedFields.has("decision"),
          }
        : undefined;
      const criteriaFields = hasCustomForm
        ? {
            customCriteriaScores: customFormCriteria!.map((c) => ({
              criterionId: c.id,
              score: criteriaScores[c.id]?.score ?? 0,
              notes: criteriaScores[c.id]?.notes || undefined,
            })),
          }
        : {
            gcaVerdict: gcaVerdict!,
            gcaNotes,
            roleKnowledgeVerdict: roleKnowledgeVerdict!,
            roleKnowledgeNotes,
            compWayVerdict: compWayVerdict!,
            compWayNotes,
          };
      if (isEditing) {
        return rpcClient.interviews.editFeedback({
          id: interviewId,
          ...criteriaFields,
          candidateExcitementScore: candidateExcitementScore ?? null,
          candidateExcitementNote: candidateExcitementNote || undefined,
          generalComments: generalComments || undefined,
          notesForNextInterviewer: notesForNextInterviewer || undefined,
          decision: decision!,
          aiDraftEditedFields: editedSnapshot,
        });
      }
      const effectiveAutoAdvance = decision === "advance" && autoAdvance && allowAutoAdvanceFlag;
      return rpcClient.interviews.submitFeedback({
        id: interviewId,
        ...criteriaFields,
        candidateExcitementScore: candidateExcitementScore ?? null,
        candidateExcitementNote: candidateExcitementNote || undefined,
        generalComments: generalComments || undefined,
        notesForNextInterviewer: notesForNextInterviewer || undefined,
        decision: decision!,
        aiDraftUsed: aiDraftApplied || undefined,
        aiDraftEditedFields: editedSnapshot,
        autoAdvance: effectiveAutoAdvance,
        advanceMode: effectiveAutoAdvance ? advanceMode : "auto",
        nextInterviewerId:
          effectiveAutoAdvance &&
          advanceMode === "choose" &&
          nextInterviewerSelection?.type === "individual"
            ? nextInterviewerSelection.id
            : undefined,
        nextInterviewerGroupId:
          effectiveAutoAdvance &&
          advanceMode === "choose" &&
          nextInterviewerSelection?.type === "group"
            ? nextInterviewerSelection.id
            : undefined,
      });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: orpc.interviews.mine.queryOptions().queryKey });
      queryClient.invalidateQueries({ queryKey: orpc.applications.list.queryOptions().queryKey });
      if (interview?.applicationId) {
        queryClient.invalidateQueries({
          queryKey: orpc.applications.getDetails.queryOptions({
            input: { id: interview.applicationId },
          }).queryKey,
        });
        queryClient.invalidateQueries({
          queryKey: orpc.interviews.getByApplication.queryOptions({
            input: { applicationId: interview.applicationId },
          }).queryKey,
        });
      }
      if (interview?.jobOpeningId) {
        queryClient.invalidateQueries({
          queryKey: orpc.applications.getJobKanban.queryOptions({
            input: { jobOpeningId: interview.jobOpeningId },
          }).queryKey,
        });
      }
      onOpenChange(false);
      resetForm();
      if (!isEditing && (data as SubmitFeedbackResponse).advanceEmailFailed) {
        toast({
          title: "Avaliação enviada",
          description:
            "O candidato foi avançado, mas o envio do email falhou. Reenvie pelo card de detalhes do candidato.",
          variant: "destructive",
        });
      } else {
        toast({
          title: isEditing ? "Avaliação atualizada com sucesso!" : "Avaliação enviada com sucesso!",
        });
      }
      onSuccess?.();
    },
    onError: (error: Error) => {
      toast({
        title: isEditing ? "Erro ao atualizar avaliação" : "Erro ao enviar avaliação",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleSubmit = () => {
    if (!interview) return;
    const validationError = validateFeedbackForm({
      decision,
      gcaVerdict,
      gcaNotes,
      roleKnowledgeVerdict,
      roleKnowledgeNotes,
      compWayVerdict,
      compWayNotes,
      autoAdvance,
      allowAutoAdvanceFlag,
      advanceMode,
      nextInterviewerSelection,
      customCriteria: customFormCriteria,
      criteriaScores,
    });
    if (validationError) {
      toast({ title: "Campo obrigatório", description: validationError, variant: "destructive" });
      return;
    }
    submitFeedback.mutate(interview.interviewId);
  };

  // ---------------- Status row computation ----------------
  const statusMeta = useMemo<DraftStatusMeta>(() => {
    if (isEditing) return { label: "", detail: "", tone: "idle" };
    if (isDraftLoading) {
      return { label: "Analisando transcript…", detail: "", tone: "generating" };
    }
    if (aiDraftApplied) {
      return {
        label: "Rascunho da IA pronto",
        detail: "· revise e ajuste o que precisar",
        tone: "ready",
      };
    }
    return { label: "", detail: "", tone: "idle" };
  }, [isEditing, isDraftLoading, aiDraftApplied]);

  const showStatusRow = !isEditing && statusMeta.tone !== "idle";

  const dotClass =
    statusMeta.tone === "ready"
      ? "bg-emerald-500"
      : statusMeta.tone === "generating"
        ? "bg-[#e6d3ae]"
        : statusMeta.tone === "warn"
          ? "bg-amber-500"
          : statusMeta.tone === "failed"
            ? "bg-red-500"
            : "bg-muted-foreground/40";

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) resetForm();
        onOpenChange(isOpen);
      }}
    >
      <DialogContent
        className="max-w-[640px] max-h-[92vh] p-0 gap-0 flex flex-col overflow-hidden rounded-[14px] tracking-[-0.0125em]"
        data-testid="dialog-feedback"
      >
        {interview && (
          <>
            {/* Header — leave 32px right margin to clear the dialog's built-in X close. */}
            <div className="flex-shrink-0 px-6 pt-5 pb-4 border-b border-border pr-12">
              <span className="text-[10px] font-semibold uppercase tracking-[0.09em] text-muted-foreground">
                {isEditing ? "Editar avaliação" : "Avaliação"}
              </span>
              <div className="mt-1.5 flex flex-wrap items-center gap-2 text-sm">
                <span className="text-muted-foreground">{interview.stageName || "Entrevista"}</span>
                <span className="text-muted-foreground/60">·</span>
                <span className="font-medium text-foreground">
                  {interview.applicantName || "Candidato"}
                </span>
                {interview.jobOpeningTitle && (
                  <>
                    <span className="text-muted-foreground/60">→</span>
                    <span className="text-muted-foreground">{interview.jobOpeningTitle}</span>
                  </>
                )}
              </div>
              <p className="mt-2.5 text-[11px] italic text-muted-foreground leading-relaxed">
                Suas respostas são{" "}
                <span className="not-italic font-medium text-foreground">confidenciais</span>.
                Apenas as notas para o próximo entrevistador são compartilhadas com a equipe.
              </p>
            </div>

            {/* Stage config still loading — don't render any step yet */}
            {stageConfigLoading && (
              <div
                className="flex-1 flex items-center justify-center px-6 py-12"
                data-testid="feedback-form-loading"
              >
                <p className="text-sm text-muted-foreground">Carregando formulário…</p>
              </div>
            )}

            {/* Step 1 — choose how to generate the evaluation */}
            {!stageConfigLoading && flowStep === "choice" && (
              <div className="flex-1 overflow-y-auto px-6 pb-6 pt-4">
                {draftMutation.isPending ? (
                  <div
                    className="flex min-h-[220px] flex-col items-center justify-center gap-3 text-center"
                    data-testid="choice-generating"
                  >
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-foreground" />
                    <p className="text-sm font-medium text-foreground">
                      Analisando a transcrição e gerando a avaliação…
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Isso pode levar alguns segundos.
                    </p>
                  </div>
                ) : (
                  <>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.09em] text-muted-foreground mb-3">
                      Como você quer gerar a avaliação?
                    </p>
                    <div className="space-y-3">
                      {/* Option A — interviewer inputs */}
                      <button
                        type="button"
                        onClick={() => setFlowStep("inputs")}
                        data-testid="button-choice-inputs"
                        className="group flex min-h-[92px] w-full flex-col justify-center rounded-[12px] border border-border px-4 py-3.5 text-left transition-all hover:-translate-y-px hover:border-foreground/40"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-foreground">
                            Adicionar meus inputs
                          </span>
                          <span className="text-xs text-muted-foreground group-hover:text-foreground">
                            →
                          </span>
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                          {hasStoredTranscript
                            ? "Você escreve suas impressões e a IA estrutura a avaliação, cruzando com a transcrição da entrevista."
                            : "Você escreve suas impressões e a IA estrutura a avaliação a partir delas."}
                        </p>
                      </button>

                      {/* Option B — transcript only */}
                      <div className="rounded-[12px] border border-border transition-colors has-[button:hover]:border-foreground/40">
                        <button
                          type="button"
                          onClick={() => {
                            if (hasStoredTranscript) {
                              draftMutation.mutate({ mode: "transcript_only" });
                            } else {
                              setShowTranscriptUrlField(true);
                            }
                          }}
                          data-testid="button-choice-transcript"
                          className="group flex min-h-[92px] w-full flex-col justify-center rounded-[12px] px-4 py-3.5 text-left"
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-foreground">
                              Avaliar só pela transcrição
                            </span>
                            <span className="text-xs text-muted-foreground group-hover:text-foreground">
                              →
                            </span>
                          </div>
                          <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                            A IA analisa a transcrição da entrevista e preenche os campos sozinha.
                          </p>
                        </button>

                        {/* Inline meeting-URL field when no transcript is stored */}
                        {!hasStoredTranscript && showTranscriptUrlField && (
                          <div className="px-4 pb-3.5 pt-0">
                            <div className="flex items-center gap-2 rounded-[10px] border border-border bg-muted/40 px-3 py-1.5">
                              <input
                                type="url"
                                value={firefliesUrlInput}
                                onChange={(e) => setFirefliesUrlInput(e.target.value)}
                                placeholder="Cole o link do Google Meet, Zoom ou Fireflies"
                                className="flex-1 bg-transparent text-xs outline-none placeholder:text-muted-foreground/60 tracking-[-0.0125em]"
                                data-testid="input-fireflies-url"
                                onKeyDown={(e) => {
                                  if (
                                    e.key === "Enter" &&
                                    firefliesUrlInput.trim() &&
                                    !draftMutation.isPending
                                  ) {
                                    e.preventDefault();
                                    draftMutation.mutate({
                                      mode: "transcript_only",
                                      url: firefliesUrlInput.trim(),
                                    });
                                  }
                                }}
                              />
                              <button
                                type="button"
                                disabled={!firefliesUrlInput.trim() || draftMutation.isPending}
                                onClick={() =>
                                  draftMutation.mutate({
                                    mode: "transcript_only",
                                    url: firefliesUrlInput.trim(),
                                  })
                                }
                                data-testid="button-generate-draft"
                                className="h-6 rounded-md px-2 text-xs font-medium text-foreground hover:bg-background disabled:opacity-40 transition-colors"
                              >
                                Gerar rascunho
                              </button>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Manual fallback */}
                      <button
                        type="button"
                        onClick={() => setFlowStep("form")}
                        data-testid="button-choice-manual"
                        className="w-full pt-1 text-center text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
                      >
                        Prefiro preencher manualmente, sem IA
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Step 2 — interviewer inputs */}
            {!stageConfigLoading && flowStep === "inputs" && (
              <div className="flex-1 overflow-y-auto px-6 py-6">
                <div className="mb-2 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setFlowStep("choice")}
                    aria-label="Voltar"
                    data-testid="button-inputs-back"
                    className="text-sm leading-none text-muted-foreground hover:text-foreground"
                  >
                    ←
                  </button>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.09em] text-muted-foreground">
                    Seus inputs
                  </p>
                </div>
                <p className="mb-3 text-xs text-muted-foreground leading-relaxed">
                  Escreva livremente suas impressões, pontos fortes, red flags e dúvidas a
                  investigar.
                </p>
                <Textarea
                  rows={7}
                  value={interviewerInputs}
                  onChange={(e) => setInterviewerInputs(e.target.value)}
                  placeholder="Ex: Mandou muito bem em raciocínio analítico — quebrou o case de pricing em partes claras. Fiquei na dúvida sobre profundidade técnica em SQL, não testei a fundo. Cultura: super alinhada, deu exemplos concretos de ownership…"
                  data-testid="input-interviewer-notes"
                  className="text-[13px] tracking-[-0.0125em]"
                />
                {hasStoredTranscript ? (
                  <p className="mt-3 rounded-[10px] border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300">
                    A transcrição da entrevista será usada como contexto para corroborar e
                    enriquecer seus inputs.
                  </p>
                ) : (
                  <div className="mt-3">
                    <p className="mb-1.5 text-xs text-muted-foreground leading-relaxed">
                      Tem gravação dessa entrevista? Cole o link (Google Meet, Zoom ou Fireflies) —
                      opcional, a IA cruza com seus inputs. Sem link, a avaliação sai só dos seus
                      inputs.
                    </p>
                    <div className="flex items-center gap-2 rounded-[10px] border border-border bg-muted/40 px-3 py-1.5">
                      <input
                        type="url"
                        value={firefliesUrlInput}
                        onChange={(e) => setFirefliesUrlInput(e.target.value)}
                        placeholder="Link do Google Meet, Zoom ou Fireflies (opcional)"
                        className="flex-1 bg-transparent text-xs outline-none placeholder:text-muted-foreground/60 tracking-[-0.0125em]"
                        data-testid="input-inputs-fireflies-url"
                      />
                    </div>
                  </div>
                )}
                <div className="mt-5 flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setFlowStep("choice")}
                    data-testid="button-inputs-cancel"
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="button"
                    disabled={!interviewerInputs.trim() || draftMutation.isPending}
                    onClick={() =>
                      draftMutation.mutate({
                        mode: "with_inputs",
                        inputs: interviewerInputs.trim(),
                        url:
                          !hasStoredTranscript && firefliesUrlInput.trim()
                            ? firefliesUrlInput.trim()
                            : undefined,
                      })
                    }
                    data-testid="button-inputs-generate"
                  >
                    {draftMutation.isPending ? "Gerando…" : "Gerar avaliação"}
                  </Button>
                </div>
              </div>
            )}

            {!stageConfigLoading && flowStep === "form" && (
              <>
                {/* Status row */}
                {showStatusRow && (
                  <div className="relative flex-shrink-0 flex items-center gap-2 px-6 py-2.5 border-b border-border bg-muted/40 text-xs text-muted-foreground overflow-hidden">
                    <span
                      className={[
                        "h-1.5 w-1.5 rounded-full flex-shrink-0",
                        dotClass,
                        statusMeta.tone === "generating" ? "animate-pulse" : "",
                      ].join(" ")}
                    />
                    <span className="font-medium text-foreground">{statusMeta.label}</span>
                    {statusMeta.detail && <span>{statusMeta.detail}</span>}
                    <span className="flex-1" />
                    {statusMeta.tone === "generating" && (
                      <span className="absolute left-0 bottom-0 h-px w-1/3 bg-[#e6d3ae] animate-ai-progress-slide" />
                    )}
                  </div>
                )}

                {/* Personal Fireflies key input — appears when the meeting is
                host-private and the user hasn't linked their own key. */}
                {showKeyInput && (
                  <div className="flex-shrink-0 px-6 pt-3 pb-1">
                    <div className="flex items-center gap-2 rounded-[10px] border border-border bg-muted/40 px-3 py-1.5">
                      <input
                        type="password"
                        value={firefliesKeyInput}
                        onChange={(e) => setFirefliesKeyInput(e.target.value)}
                        placeholder="Cole sua API key pessoal do Fireflies"
                        className="flex-1 bg-transparent text-xs outline-none placeholder:text-muted-foreground/60 tracking-[-0.0125em]"
                        data-testid="input-fireflies-key"
                        onKeyDown={(e) => {
                          if (
                            e.key === "Enter" &&
                            firefliesKeyInput.trim() &&
                            !connectFirefliesMutation.isPending
                          ) {
                            e.preventDefault();
                            connectFirefliesMutation.mutate(firefliesKeyInput.trim());
                          }
                        }}
                      />
                      <button
                        type="button"
                        disabled={!firefliesKeyInput.trim() || connectFirefliesMutation.isPending}
                        onClick={() => connectFirefliesMutation.mutate(firefliesKeyInput.trim())}
                        data-testid="button-save-fireflies-key"
                        className="h-6 rounded-md px-2 text-xs font-medium text-foreground hover:bg-background disabled:opacity-40 transition-colors"
                      >
                        {connectFirefliesMutation.isPending ? "Conectando…" : "Conectar"}
                      </button>
                    </div>
                    <p className="mt-1 px-1 text-[10px] text-muted-foreground/70">
                      Em Fireflies → Settings → Developer. Usada só para ler suas próprias reuniões
                      privadas; nunca é compartilhada com o workspace.
                    </p>
                  </div>
                )}

                {/* Meeting-URL entry now lives on the transcript-only card in the
                choice step — no in-form manual URL block. */}

                {/* Scrollable body */}
                <div
                  className={[
                    "flex-1 overflow-y-auto px-6 transition-opacity duration-200",
                    isDraftLoading ? "opacity-40 pointer-events-none" : "",
                  ].join(" ")}
                >
                  {/* Criteria — custom form (per-stage) or default Comp form */}
                  {hasCustomForm ? (
                    <div className="border-b border-border">
                      {customFormCriteria!.map((criterion, idx) => (
                        <CustomCriterionRow
                          key={criterion.id}
                          criterion={criterion}
                          value={criteriaScores[criterion.id] ?? { score: null, notes: "" }}
                          onChange={(entry) =>
                            setCriteriaScores((prev) => ({ ...prev, [criterion.id]: entry }))
                          }
                          showFirstBorder={idx !== 0}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="border-b border-border">
                      <Criterion
                        testIdPrefix="gca"
                        title="General Cognitive Ability"
                        subtitle="Raciocínio lógico, decomposição de problemas, clareza de pensamento."
                        verdict={gcaVerdict}
                        onVerdictChange={setGcaVerdictE}
                        notes={gcaNotes}
                        onNotesChange={(v) => {
                          setGcaNotes(v);
                          markEdited("gca");
                        }}
                        notesPlaceholder="Comentário sobre GCA…"
                        notesTestId="input-gca-notes"
                        isAi={aiDraftApplied}
                        edited={editedFields.has("gca")}
                        feedback={null}
                        onFeedback={null}
                        showFirstBorder={false}
                      />
                      <Criterion
                        testIdPrefix="role-knowledge"
                        title="Role-Related Knowledge"
                        subtitle="Profundidade no domínio da vaga, exemplos concretos, calibração de expertise."
                        verdict={roleKnowledgeVerdict}
                        onVerdictChange={setRoleKnowledgeVerdictE}
                        notes={roleKnowledgeNotes}
                        onNotesChange={(v) => {
                          setRoleKnowledgeNotes(v);
                          markEdited("role");
                        }}
                        notesPlaceholder="Comentário sobre experiência profissional…"
                        notesTestId="input-role-knowledge-notes"
                        isAi={aiDraftApplied}
                        edited={editedFields.has("role")}
                        feedback={null}
                        onFeedback={null}
                      />
                      <Criterion
                        testIdPrefix="comp-way"
                        title="COMP Way"
                        subtitle="Ownership, bias for action, honestidade intelectual, foco em impacto."
                        verdict={compWayVerdict}
                        onVerdictChange={setCompWayVerdictE}
                        notes={compWayNotes}
                        onNotesChange={(v) => {
                          setCompWayNotes(v);
                          markEdited("comp");
                        }}
                        notesPlaceholder="Comentário sobre COMP Way…"
                        notesTestId="input-comp-way-notes"
                        isAi={aiDraftApplied}
                        edited={editedFields.has("comp")}
                        feedback={null}
                        onFeedback={null}
                      />
                    </div>
                  )}

                  {/* Notes section */}
                  <div className="py-2 border-b border-border">
                    <NoteBlock
                      label="Comentários gerais"
                      help="Impressão consolidada após a entrevista."
                      value={generalComments}
                      onChange={(v) => {
                        setGeneralComments(v);
                        markEdited("general");
                      }}
                      testId="input-general-comments"
                      placeholder="Impressões sobre o candidato…"
                      isAi={aiDraftApplied}
                      edited={editedFields.has("general")}
                      feedback={null}
                      onFeedback={null}
                      showFirstBorder={false}
                    />
                    <NoteBlock
                      label="Para o próximo entrevistador"
                      help={
                        <>
                          <span className="font-medium text-foreground not-italic">
                            Visível para a equipe.
                          </span>{" "}
                          Gaps, sinais a validar, pontos a aprofundar.
                        </>
                      }
                      value={notesForNextInterviewer}
                      onChange={(v) => {
                        setNotesForNextInterviewer(v);
                        markEdited("next");
                      }}
                      testId="input-notes-next-interviewer"
                      placeholder="Orientações para a próxima etapa…"
                      isAi={aiDraftApplied}
                      edited={editedFields.has("next")}
                      feedback={null}
                      onFeedback={null}
                    />
                  </div>

                  {/* Candidate excitement — interviewer's own reading. Sits right
                  before the decision because it directly informs both
                  "advance/reject" and the eventual offer calibration.
                  Optional: leave blank if you didn't get a clear read. */}
                  <div className="py-3 border-b border-border">
                    <div className="flex items-start justify-between gap-3 mb-1.5">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-foreground">
                          Animação do candidato
                        </div>
                        <p className="mt-0.5 text-[11px] text-muted-foreground leading-relaxed">
                          Sua leitura de quão animado o candidato está com a Comp (0–10). Usado pra
                          calibrar a oferta. Deixe em branco se não pegou sinal claro.
                        </p>
                      </div>
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <div className="flex items-center gap-1">
                        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => {
                          const active = candidateExcitementScore === n;
                          return (
                            <button
                              key={n}
                              type="button"
                              onClick={() => setCandidateExcitementScore(active ? null : n)}
                              className={[
                                "inline-flex h-7 w-7 items-center justify-center rounded-md text-xs font-semibold transition-all",
                                active
                                  ? "bg-foreground text-background"
                                  : "text-muted-foreground hover:bg-muted hover:text-foreground",
                              ].join(" ")}
                              data-testid={`button-excitement-${n}`}
                            >
                              {n}
                            </button>
                          );
                        })}
                      </div>
                      {candidateExcitementScore !== null && (
                        <button
                          type="button"
                          onClick={() => setCandidateExcitementScore(null)}
                          className="ml-2 text-[11px] text-muted-foreground hover:text-foreground"
                          data-testid="button-excitement-clear"
                        >
                          Limpar
                        </button>
                      )}
                    </div>
                    {candidateExcitementScore != null ? (
                      <AutosizeTextarea
                        value={candidateExcitementNote}
                        onChange={(e) => setCandidateExcitementNote(e.target.value)}
                        placeholder="O que mais te marcou (1 linha)…"
                        className="mt-2 text-sm"
                        minHeight={36}
                        maxHeight={240}
                        data-testid="input-candidate-excitement-note"
                      />
                    ) : null}
                  </div>

                  {/* Resumo + Decisão */}
                  <div className="my-4 rounded-[14px] border border-border bg-muted/40 px-4 py-4">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.09em] text-muted-foreground mb-3">
                      Resumo
                    </div>
                    {/* The default-form verdict chips only make sense when the
                    stage uses the default Comp form — custom-form criteria are
                    already filled above. */}
                    {!hasCustomForm && (
                      <div className="flex flex-wrap gap-x-6 gap-y-3 mb-4">
                        <SummaryVerdict
                          label="GCA"
                          value={gcaVerdict}
                          onChange={setGcaVerdictE}
                          testIdPrefix="summary-gca"
                        />
                        <SummaryVerdict
                          label="Role"
                          value={roleKnowledgeVerdict}
                          onChange={setRoleKnowledgeVerdictE}
                          testIdPrefix="summary-role"
                        />
                        <SummaryVerdict
                          label="COMP Way"
                          value={compWayVerdict}
                          onChange={setCompWayVerdictE}
                          testIdPrefix="summary-comp"
                        />
                      </div>
                    )}
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setDecisionE("reject")}
                        data-testid="button-decision-reject"
                        className={[
                          "flex-1 h-10 rounded-[10px] border text-[13px] font-medium tracking-[-0.0125em]",
                          "transition-all duration-150 ease-out",
                          decision === "reject"
                            ? "bg-foreground text-background border-foreground"
                            : "bg-background text-foreground border-border hover:-translate-y-px hover:border-foreground/30",
                        ].join(" ")}
                      >
                        Rejeitar
                      </button>
                      <button
                        type="button"
                        onClick={() => setDecisionE("advance")}
                        data-testid="button-decision-advance"
                        className={[
                          "flex-1 h-10 rounded-[10px] border text-[13px] font-medium tracking-[-0.0125em]",
                          "transition-all duration-150 ease-out",
                          decision === "advance"
                            ? "bg-foreground text-background border-foreground"
                            : "bg-background text-foreground border-border hover:-translate-y-px hover:border-foreground/30",
                        ].join(" ")}
                      >
                        Aprovar
                      </button>
                    </div>

                    {decision === "advance" &&
                      !interview?.interviewerGroupId &&
                      !isEditing &&
                      allowAutoAdvanceFlag && (
                        <div className="mt-5 border-t border-border pt-4">
                          <div className="flex items-start gap-3">
                            <Checkbox
                              id="autoAdvance"
                              checked={autoAdvance}
                              onCheckedChange={(checked) => {
                                setAutoAdvance(checked === true);
                                if (!checked) {
                                  setAdvanceMode("auto");
                                  setNextInterviewerSelection(null);
                                }
                              }}
                              className="mt-0.5"
                              data-testid="checkbox-auto-advance"
                            />
                            <div className="flex-1 space-y-0.5">
                              <label
                                htmlFor="autoAdvance"
                                className="text-sm font-medium leading-none cursor-pointer"
                              >
                                Já encaminhar para a próxima etapa
                              </label>
                              <p className="text-xs text-muted-foreground">
                                Caso contrário, alguém vai cuidar do roteamento depois.
                              </p>
                            </div>
                          </div>
                          {autoAdvance && (
                            <div className="mt-4 pl-7 space-y-3">
                              <p className="text-[11px] uppercase tracking-wider text-muted-foreground/70 font-medium">
                                Quem vai entrevistar
                              </p>
                              <RadioGroup
                                value={advanceMode}
                                onValueChange={(val) => {
                                  setAdvanceMode(val as "auto" | "choose" | "delegate");
                                  if (val !== "choose") setNextInterviewerSelection(null);
                                }}
                                className="space-y-2"
                                data-testid="radio-advance-mode"
                              >
                                <label
                                  htmlFor="advance-auto"
                                  className="flex items-center gap-2.5 cursor-pointer"
                                >
                                  <RadioGroupItem
                                    value="auto"
                                    id="advance-auto"
                                    data-testid="radio-advance-auto"
                                  />
                                  <span className="text-sm">Round-robin automático</span>
                                </label>
                                <label
                                  htmlFor="advance-choose"
                                  className="flex items-center gap-2.5 cursor-pointer"
                                >
                                  <RadioGroupItem
                                    value="choose"
                                    id="advance-choose"
                                    data-testid="radio-advance-choose"
                                  />
                                  <span className="text-sm">Escolher entrevistador</span>
                                </label>
                                <label
                                  htmlFor="advance-delegate"
                                  className="flex items-center gap-2.5 cursor-pointer"
                                >
                                  <RadioGroupItem
                                    value="delegate"
                                    id="advance-delegate"
                                    data-testid="radio-advance-delegate"
                                  />
                                  <span className="text-sm">Delegar ao Hiring Manager</span>
                                </label>
                              </RadioGroup>
                              {advanceMode === "choose" && (
                                <Select
                                  value={
                                    nextInterviewerSelection
                                      ? `${nextInterviewerSelection.type}:${nextInterviewerSelection.id}`
                                      : ""
                                  }
                                  onValueChange={(val) => {
                                    const [type, id] = val.split(":");
                                    setNextInterviewerSelection({
                                      type: type as "individual" | "group",
                                      id,
                                    });
                                  }}
                                >
                                  <SelectTrigger
                                    className="w-full h-9 text-sm"
                                    data-testid="select-next-interviewer-trigger"
                                  >
                                    <SelectValue placeholder="Selecione o entrevistador..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {nextStageOptions.map((opt) => (
                                      <SelectItem
                                        key={`${opt.type}:${opt.id}`}
                                        value={`${opt.type}:${opt.id}`}
                                        data-testid={`select-interviewer-${opt.id}`}
                                      >
                                        {opt.label}{" "}
                                        <span className="text-muted-foreground">
                                          · {opt.count} entrevistas
                                        </span>
                                      </SelectItem>
                                    ))}
                                    {nextStageOptions.length === 0 && (
                                      <SelectItem value="__none" disabled>
                                        Nenhum entrevistador disponível
                                      </SelectItem>
                                    )}
                                  </SelectContent>
                                </Select>
                              )}
                              {advanceMode === "delegate" && (
                                <p className="text-xs text-muted-foreground leading-relaxed">
                                  O Hiring Manager será notificado e decidirá o próximo passo.
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                    {/* Overall AI utility — only when an AI draft was actually
                    applied. Coarse signal that feeds the rejection-analysis
                    digest; per-field "where" comes from the implicit edit-diff. */}
                    {aiDraftApplied && !isEditing && (
                      <div className="mt-5 flex items-center gap-2 text-xs">
                        <span className="text-muted-foreground">
                          O rascunho da IA te economizou tempo?
                        </span>
                        <div className="flex gap-1.5">
                          {(["good", "bad"] as const).map((v) => {
                            const on = overallAiFeedback === v;
                            const label = v === "good" ? "Sim" : "Não";
                            return (
                              <button
                                key={v}
                                type="button"
                                onClick={() => setOverallFeedback(v)}
                                data-testid={`button-overall-ai-feedback-${v}`}
                                className={[
                                  "h-[22px] rounded-full px-2.5 text-[11px] font-medium leading-none",
                                  "border transition-all duration-150 ease-out tracking-[-0.0125em]",
                                  on
                                    ? "bg-foreground text-background border-foreground"
                                    : "bg-background text-muted-foreground border-border hover:-translate-y-px hover:text-foreground",
                                ].join(" ")}
                              >
                                {label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Footer */}
                <DialogFooter className="flex-shrink-0 px-6 py-3 border-t border-border">
                  <Button
                    variant="ghost"
                    onClick={() => {
                      onOpenChange(false);
                      resetForm();
                    }}
                    data-testid="button-cancel-feedback"
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={submitFeedback.isPending}
                    data-testid="button-submit-feedback"
                  >
                    {submitFeedback.isPending
                      ? isEditing
                        ? "Salvando…"
                        : "Enviando…"
                      : isEditing
                        ? "Salvar alterações"
                        : "Enviar avaliação"}
                  </Button>
                </DialogFooter>
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ------------------------------------------------------------------
// Internal subcomponents — kept private to this file. The criterion +
// note-block patterns share the AI tint + micro-feedback shape.
// ------------------------------------------------------------------

function Criterion(props: {
  testIdPrefix: string;
  title: string;
  subtitle: string;
  verdict: Verdict;
  onVerdictChange: (v: Verdict) => void;
  notes: string;
  onNotesChange: (v: string) => void;
  notesPlaceholder: string;
  notesTestId: string;
  isAi: boolean;
  edited: boolean;
  feedback: "good" | "bad" | null;
  onFeedback: ((v: "good" | "bad") => void) | null;
  showFirstBorder?: boolean;
}) {
  const showMicro = props.isAi && !props.edited && props.onFeedback;
  return (
    <div
      className={["py-4", props.showFirstBorder === false ? "" : "border-t border-border"].join(
        " ",
      )}
    >
      <div className="flex items-center justify-between gap-4 mb-1 min-h-[28px]">
        <span className="text-[10px] font-semibold uppercase tracking-[0.09em] text-foreground">
          {props.title}
        </span>
        <VerdictChips
          value={props.verdict}
          onChange={props.onVerdictChange}
          testIdPrefix={props.testIdPrefix}
        />
      </div>
      <p className="text-xs text-muted-foreground mb-2.5 leading-relaxed">{props.subtitle}</p>
      <AiField isAi={props.isAi} edited={props.edited}>
        <AutosizeTextarea
          placeholder={props.notesPlaceholder}
          value={props.notes}
          onChange={(e) => props.onNotesChange(e.target.value)}
          minHeight={56}
          data-testid={props.notesTestId}
          className="rounded-[10px] text-[13px] tracking-[-0.0125em] bg-transparent border-transparent focus-visible:border-foreground focus-visible:bg-background"
        />
        {showMicro && (
          <div className="px-3 pb-2">
            <MicroFeedback value={props.feedback} onChange={props.onFeedback!} />
          </div>
        )}
      </AiField>
      {props.isAi && props.edited && (
        <p className="mt-1.5 text-[10.5px] text-muted-foreground/70">Editado por você</p>
      )}
    </div>
  );
}

function CustomCriterionRow({
  criterion,
  value,
  onChange,
  showFirstBorder,
}: {
  criterion: StageFeedbackCriterion;
  value: { score: number | null; notes: string };
  onChange: (entry: { score: number | null; notes: string }) => void;
  showFirstBorder?: boolean;
}) {
  const min = criterion.scaleType === "binary" ? 0 : (criterion.scaleMin ?? 0);
  const max = criterion.scaleType === "binary" ? 1 : (criterion.scaleMax ?? 5);
  const binaryChip = (score: number, label: string, suffix: string) => {
    const on = value.score === score;
    return (
      <button
        type="button"
        onClick={() => onChange({ ...value, score })}
        data-testid={`button-criterion-${criterion.id}-${suffix}`}
        className={[
          "h-6 rounded-full px-3 text-[11px] font-medium leading-none",
          "border transition-all duration-150 ease-out tracking-[-0.0125em]",
          on
            ? "bg-foreground text-background border-foreground"
            : "bg-background text-muted-foreground border-border hover:-translate-y-px hover:text-foreground hover:border-foreground/30",
        ].join(" ")}
      >
        {label}
      </button>
    );
  };
  return (
    <div className={["py-4", showFirstBorder === false ? "" : "border-t border-border"].join(" ")}>
      <div className="flex items-center justify-between gap-4 mb-1 min-h-[28px]">
        <span className="text-[10px] font-semibold uppercase tracking-[0.09em] text-foreground">
          {criterion.label}
        </span>
        {criterion.scaleType === "binary" ? (
          <div className="inline-flex gap-1" role="radiogroup">
            {binaryChip(1, "Sim", "yes")}
            {binaryChip(0, "Não", "no")}
          </div>
        ) : (
          <div className="flex items-center gap-1">
            {Array.from({ length: max - min + 1 }, (_, i) => min + i).map((n) => {
              const active = value.score === n;
              return (
                <button
                  key={n}
                  type="button"
                  onClick={() => onChange({ ...value, score: active ? null : n })}
                  className={[
                    "inline-flex h-7 w-7 items-center justify-center rounded-md text-xs font-semibold transition-all",
                    active
                      ? "bg-foreground text-background"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground",
                  ].join(" ")}
                  data-testid={`button-criterion-${criterion.id}-${n}`}
                >
                  {n}
                </button>
              );
            })}
          </div>
        )}
      </div>
      {criterion.description && (
        <p className="text-xs text-muted-foreground mb-2.5 leading-relaxed">
          {criterion.description}
        </p>
      )}
      <AutosizeTextarea
        // biome-ignore lint/security/noSecrets: false positive — UI placeholder, not a secret
        placeholder={criterion.requireNotes ? "Comentário…" : "Comentário (opcional)…"}
        value={value.notes}
        onChange={(e) => onChange({ ...value, notes: e.target.value })}
        minHeight={56}
        data-testid={`input-criterion-notes-${criterion.id}`}
        className="rounded-[10px] text-[13px] tracking-[-0.0125em] bg-transparent border-transparent focus-visible:border-foreground focus-visible:bg-background"
      />
    </div>
  );
}

function NoteBlock(props: {
  label: string;
  help: React.ReactNode;
  value: string;
  onChange: (v: string) => void;
  testId: string;
  placeholder: string;
  isAi: boolean;
  edited: boolean;
  feedback: "good" | "bad" | null;
  onFeedback: ((v: "good" | "bad") => void) | null;
  showFirstBorder?: boolean;
}) {
  const showMicro = props.isAi && !props.edited && props.onFeedback;
  return (
    <div
      className={["py-4", props.showFirstBorder === false ? "" : "border-t border-border"].join(
        " ",
      )}
    >
      <div className="text-[10px] font-semibold uppercase tracking-[0.09em] text-foreground mb-1">
        {props.label}
      </div>
      <p className="text-xs text-muted-foreground mb-2.5 leading-relaxed italic">{props.help}</p>
      <AiField isAi={props.isAi} edited={props.edited}>
        <AutosizeTextarea
          placeholder={props.placeholder}
          value={props.value}
          onChange={(e) => props.onChange(e.target.value)}
          minHeight={72}
          collapsedMaxHeight={160}
          maxHeight={480}
          data-testid={props.testId}
          className="rounded-[10px] text-[13px] tracking-[-0.0125em] bg-transparent border-transparent focus-visible:border-foreground focus-visible:bg-background"
        />
        {showMicro && (
          <div className="px-3 pb-2">
            <MicroFeedback value={props.feedback} onChange={props.onFeedback!} />
          </div>
        )}
      </AiField>
      {props.isAi && props.edited && (
        <p className="mt-1.5 text-[10.5px] text-muted-foreground/70">Editado por você</p>
      )}
    </div>
  );
}

function SummaryVerdict({
  label,
  value,
  onChange,
  testIdPrefix,
}: {
  label: string;
  value: Verdict;
  onChange: (v: Verdict) => void;
  testIdPrefix: string;
}) {
  // Collapsed by default: shows a single pill pre-filled with the current
  // (AI-suggested) verdict. Clicking it expands the Sim/Sem sinal/Não options;
  // picking one collapses back to the pill.
  const [editing, setEditing] = useState(false);

  if (editing) {
    return (
      <div className="flex items-center gap-2 text-xs">
        <span className="text-muted-foreground">{label}</span>
        <VerdictChips
          value={value}
          onChange={(v) => {
            onChange(v);
            setEditing(false);
          }}
          testIdPrefix={testIdPrefix}
        />
      </div>
    );
  }

  const text =
    value === "YES"
      ? "Sim"
      : value === "NO"
        ? "Não"
        : value === "UNKNOWN"
          ? "Sem sinal"
          : "Definir";
  const on = value === "YES" || value === "NO";
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <button
        type="button"
        onClick={() => setEditing(true)}
        data-testid={`button-${testIdPrefix}-pill`}
        className={[
          "px-2.5 py-0.5 rounded-full text-[11px] font-medium border tracking-[-0.0125em]",
          "transition-all duration-150 ease-out hover:-translate-y-px",
          on
            ? "bg-foreground text-background border-foreground"
            : "bg-background text-muted-foreground border-border hover:border-foreground/30 hover:text-foreground",
        ].join(" ")}
      >
        {text}
      </button>
    </div>
  );
}
